/*
 * Handwritten cards in the cart drawer and on the cart page - one per
 * bouquet / arrangement / bundle. (The card message is no longer asked for on
 * the product page; this is where customers write it.)
 *
 * WHERE THE TEXT GOES
 *   The line item's `Message` property. That is the same field the Globo
 *   (GPO) product-page option writes, the order queue reads per arrangement
 *   (shared/models/order.ts cardMessageFrom) and the Order Printer sheet prints
 *   (li.properties['Message']). Never rename it.
 *
 * WHICH LINES GET A CARD
 *   Products tagged "Floral Arrangement" or "Bouquet" (the queue's isFloral
 *   test), "Bundles" or "Bulk Bundles". Also any line that already has a
 *   message or came from a GPO option set.
 *
 * QUANTITY > 1
 *   One line = one card. If a line has 2+ of the same arrangement, the customer
 *   can tick "Write a separate card for each"; saving then splits the line into
 *   single units, each with its own Message (the queue lists these as
 *   "(1 of 2)", "(2 of 2)").
 *
 * NOTHING TYPED IS LOST (2 Oct 2026)
 *   Until 2 Oct, text only reached the cart when "Save card" was pressed, so a
 *   customer who typed a message and went straight to Checkout lost it (8 of 20
 *   card orders 30 Sep-2 Oct had no Message). Now:
 *   1. Auto-save while typing - a second after the customer stops typing,
 *      the text is saved to the cart quietly (no cart re-render, so the box
 *      stays open and the iPad/phone keyboard stays up); "Saved" shows by the
 *      Save button. Not used for "separate card for each" (that splits lines
 *      and needs a re-render) - those save on tap-away / checkout / Save.
 *      iPad Safari does NOT blur a textarea when you tap a blank part of the
 *      page, so blur alone was not enough (v3, 2 Oct).
 *   2. Tap-away save - a tap/click anywhere outside the card (or focus
 *      leaving it) saves and closes the box. Taps on the card's own Save /
 *      Cancel / "separate card" controls are left to those controls.
 *   3. Checkout guard - a click on Checkout / View cart / an express-pay
 *      button while any card has unsaved text (or a save is still in flight)
 *      is held, the text is saved, then the same button is clicked again so
 *      Zapiet's own checks still run. If a save fails, checkout is held and the
 *      error shows on the card.
 *
 * KEYBOARD (3 Oct 2026)
 *   On touch devices, focusing a card box scrolls it to the middle of the
 *   space left above the on-screen keyboard (visualViewport), in the drawer's
 *   own scroll area or the page (cart page). Desktop is left alone.
 *
 * Markup: the [data-vb-card] placeholders in snippets/site-template.liquid
 * (drawer, #ajaxcart-template) and templates/cart.liquid (cart page,
 * #cart-form-template). Both are rendered by wpbingo.js; cards are
 * re-initialised here whenever either re-renders.
 */
(function () {
	'use strict';

	var MAX_LEN = 200; // matches the GPO Message field
	var CARD_TAGS = ['Floral Arrangement', 'Bouquet', 'Bundles', 'Bulk Bundles'];
	var TAG_CACHE_KEY = 'vbCardHandles';
	var PLACEHOLDER = 'e.g. Happy birthday, Mum. Love always, Sam';
	// Buttons/links that leave the cart. Clicks on these wait for card saves.
	var LEAVE_SELECTOR = [
		'[name="checkout"]', '[name="goto_pp"]', '[name="goto_gc"]',
		'.cart-modal__btn--viewcart', '.cart-modal__btn--checkout',
		'a[href*="/checkout"]', '.additional_checkout_buttons',
		'[data-shopify="payment-button"]', 'shopify-accelerated-checkout',
		'shopify-accelerated-checkout-cart'
	].join(',');

	var drafts = {};   // line key -> { open, split, texts[] } survives re-renders
	var floralCache = readCache();
	var holding = false;      // a leave-the-cart click is waiting on saves
	var leaving = false;      // page is unloading (checkout under way)
	var lastPointer = null;   // element under the most recent pointerdown
	var lastPointerAt = 0;
	var inflight = [];        // card saves not yet finished
	var TYPE_PAUSE = 1000;    // ms after the last keystroke before a quiet save

	function readCache() {
		try { return JSON.parse(sessionStorage.getItem(TAG_CACHE_KEY) || '{}'); } catch (e) { return {}; }
	}
	function writeCache() {
		try { sessionStorage.setItem(TAG_CACHE_KEY, JSON.stringify(floralCache)); } catch (e) {}
	}

	function isFloral(handle) {
		if (!handle) return Promise.resolve(false);
		if (Object.prototype.hasOwnProperty.call(floralCache, handle)) return Promise.resolve(floralCache[handle]);
		return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
			.then(function (r) { return r.ok ? r.json() : { tags: [] }; })
			.then(function (p) {
				var tags = p.tags || [];
				var floral = tags.some(function (t) { return CARD_TAGS.indexOf(t) !== -1; });
				floralCache[handle] = floral;
				writeCache();
				return floral;
			})
			.catch(function () { return false; });
	}

	/* ---------- cart API ---------- */

	function getCart() {
		return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
			.then(function (r) { return r.json(); });
	}
	function post(url, body) {
		return fetch(url, {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body)
		}).then(function (r) {
			if (!r.ok) return r.json().catch(function () { return {}; }).then(function (j) {
				throw new Error(j.description || j.message || ('Request failed (' + r.status + ')'));
			});
			return r.json();
		});
	}

	function clean(text) { return String(text || '').trim().slice(0, MAX_LEN); }

	// Line properties with Message set (or removed when blank). Keeps every
	// other property - incl. GPO's hidden _ ones that carry add-on pricing.
	function withMessage(props, text) {
		var out = {};
		for (var k in props) {
			if (String(k).trim().toLowerCase() !== 'message') out[k] = props[k];
		}
		var t = clean(text);
		if (t) out.Message = t;
		if (out._gpo_options) {
			try {
				var o = JSON.parse(out._gpo_options);
				if (t) o.Message = t; else delete o.Message;
				out._gpo_options = JSON.stringify(o);
			} catch (e) {}
		}
		return out;
	}

	// Resolves with the new key of the (first) saved line.
	function save(key, texts, split) {
		return getCart().then(function (cart) {
			var line = null, idx = -1;
			cart.items.forEach(function (i, n) { if (i.key === key) { line = i; idx = n; } });
			if (!line) throw new Error('This item is no longer in your cart.');
			var props = line.properties || {};
			var first = clean(texts[0]);
			// change.js keeps a line's position, so the new key is at the same index.
			var newKey = function (c) {
				var items = c.items || [];
				var at = items[idx];
				if (at && at.variant_id === line.variant_id) return at.key;
				var found = null;
				items.forEach(function (i) {
					if (!found && i.variant_id === line.variant_id && clean((i.properties || {}).Message) === first) found = i.key;
				});
				return found;
			};

			if (!split || line.quantity < 2) {
				return post('/cart/change.js', { id: key, quantity: line.quantity, properties: withMessage(props, texts[0]) })
					.then(newKey);
			}
			// Split into single units, one card each.
			var k1;
			return post('/cart/change.js', { id: key, quantity: 1, properties: withMessage(props, texts[0]) })
				.then(function (c) {
					k1 = newKey(c);
					var items = [];
					for (var n = 1; n < line.quantity; n++) {
						items.push({ id: line.variant_id, quantity: 1, properties: withMessage(props, texts[n]) });
					}
					return post('/cart/add.js', { items: items });
				})
				.then(function () { return k1; });
		});
	}

	// Re-render whichever cart views are on the page (drawer, cart page).
	function refreshCart() {
		if (window.ajaxCart && typeof window.ajaxCart.load === 'function') window.ajaxCart.load();
		if (document.querySelector('[data-cartpage-container]') && window.ajaxCartPage && typeof window.ajaxCartPage.load === 'function') {
			window.ajaxCartPage.load();
		}
	}

	/* ---------- saving (Save button, auto-save, checkout guard) ---------- */

	function savedText(card) {
		var s = card.querySelector('[data-vb-card-saved]');
		return s ? s.textContent.trim() : '';
	}

	function collect(card, d) {
		var areas = card.querySelectorAll('.vb-card__input');
		for (var i = 0; i < areas.length; i++) d.texts[i] = areas[i].value;
	}

	// Does this open card hold text that isn't in the cart yet?
	function isDirty(card) {
		if (!card.classList.contains('is-open')) return false;
		var d = drafts[card.getAttribute('data-key')];
		if (!d) return false;
		collect(card, d);
		var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
		if (d.split && qty > 1) {
			return d.texts.slice(0, qty).some(function (t) { return clean(t) !== ''; });
		}
		return clean(d.texts[0]) !== savedText(card);
	}

	// Save one card. Only one save per card at a time; a second request while
	// one is running gets the same promise.
	function commit(card) {
		clearTimeout(card._vbTimer);
		if (card._vbSaving) { // a quiet save is running - finish it, then save the rest
			return card._vbSaving.then(function () { return commit(card); }, function () { return commit(card); });
		}
		// Nothing new to save (incl. a card an earlier save already closed) -
		// never write an empty Message over a saved one by accident.
		if (!isDirty(card)) return Promise.resolve();
		var key = card.getAttribute('data-key');
		var d = drafts[key];
		collect(card, d);
		var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
		var split = d.split && qty > 1;
		var count = split ? qty : 1;
		var texts = d.texts.slice(0, count);

		var btn = card.querySelector('.vb-card__save');
		var error = card.querySelector('.vb-card__error');
		if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
		if (error) error.hidden = true;

		var p = save(key, texts, split)
			.then(function (newKey) {
				card._vbSaving = null;
				delete drafts[key]; // the line key changes once its properties do
				if (!holding) { refreshCart(); return; }
				// Checkout is waiting: don't re-render the cart under the button.
				// Show the card as saved in place and point it at the new line key.
				if (newKey) card.setAttribute('data-key', newKey);
				var s = card.querySelector('[data-vb-card-saved]');
				if (s) s.textContent = clean(texts[0]);
				drafts[card.getAttribute('data-key')] = { open: false, split: false, texts: [] };
				render(card);
			}, function (err) {
				card._vbSaving = null;
				if (btn) { btn.disabled = false; btn.textContent = 'Save card' + (count > 1 ? 's' : ''); }
				if (error) {
					error.textContent = (err && err.message) || 'That didn’t save. Please try again.';
					error.hidden = false;
				}
				throw err;
			});
		card._vbSaving = p;
		inflight.push(p);
		var done = function () { inflight = inflight.filter(function (x) { return x !== p; }); };
		p.then(done, done);
		return p;
	}

	function setSaved(card, text) {
		var s = card.querySelector('[data-vb-card-saved]');
		if (s) s.textContent = text;
	}
	function status(card, text) {
		var s = card.querySelector('.vb-card__status');
		if (s) s.textContent = text;
	}

	// Save while the box stays open: no re-render, focus and keyboard untouched.
	function quietSave(card) {
		clearTimeout(card._vbTimer);
		if (!card.isConnected || card._vbSaving || !isDirty(card)) return;
		var key = card.getAttribute('data-key');
		var d = drafts[key];
		var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
		if (d.split && qty > 1) return;
		var text = clean(d.texts[0]);
		status(card, 'Saving…');
		var p = save(key, [text], false).then(function (newKey) {
			card._vbSaving = null;
			if (newKey && newKey !== key) {
				card.setAttribute('data-key', newKey);
				drafts[newKey] = drafts[key];
				delete drafts[key];
			}
			setSaved(card, text);
			status(card, text ? 'Saved' : '');
			if (isDirty(card)) schedule(card); // kept typing while it saved
		}, function () {
			card._vbSaving = null;
			status(card, '');
		});
		card._vbSaving = p;
		inflight.push(p);
		var done = function () { inflight = inflight.filter(function (x) { return x !== p; }); };
		p.then(done, done);
	}
	function schedule(card) {
		clearTimeout(card._vbTimer);
		card._vbTimer = setTimeout(function () { quietSave(card); }, TYPE_PAUSE);
	}

	/* ---------- UI ---------- */

	/* ---------- keyboard: centre the box above it ---------- */

	var TOUCH = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || navigator.maxTouchPoints > 0;

	function scrollParent(node) {
		for (var n = node.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
			var oy = getComputedStyle(n).overflowY;
			if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
		}
		return null;
	}

	// Middle of the box -> middle of what's visible above the keyboard.
	function centre(ta) {
		if (document.activeElement !== ta || !ta.isConnected) return;
		var vv = window.visualViewport;
		var top = vv ? vv.offsetTop : 0;
		var h = vv ? vv.height : window.innerHeight;
		var r = ta.getBoundingClientRect();
		var delta = (r.top + r.height / 2) - (top + h / 2);
		if (Math.abs(delta) < 6) return;
		var sc = scrollParent(ta);
		if (sc) sc.scrollTop += delta;
		else window.scrollBy(0, delta);
	}

	// The keyboard animates in (~300ms) and the browser does its own scroll
	// first, so centre after it settles: on each viewport resize for the next
	// second, plus fallbacks.
	function centreWhenKeyboardUp(ta) {
		if (!TOUCH) return;
		var vv = window.visualViewport;
		var timer;
		var later = function () { clearTimeout(timer); timer = setTimeout(function () { centre(ta); }, 80); };
		if (vv) {
			vv.addEventListener('resize', later);
			setTimeout(function () { vv.removeEventListener('resize', later); }, 1000);
		}
		setTimeout(function () { centre(ta); }, 350);
		setTimeout(function () { centre(ta); }, 700);
	}

	function el(tag, cls, text) {
		var e = document.createElement(tag);
		if (cls) e.className = cls;
		if (text != null) e.textContent = text;
		return e;
	}

	function counter(textarea, out) {
		var update = function () { out.textContent = textarea.value.length + ' / ' + MAX_LEN; };
		textarea.addEventListener('input', update);
		update();
	}

	function render(card) {
		var key = card.getAttribute('data-key');
		var qty = parseInt(card.getAttribute('data-qty'), 10) || 1;
		var saved = savedText(card);
		// Empty cards start OPEN (ready to type in) unless Theme settings >
		// Handwritten cards > "Card box starts open when empty" is off. Never
		// auto-focused, so a phone keyboard only appears when the customer taps in.
		// Saved cards start closed, showing the message + Edit.
		var startOpen = window.vbCardStartOpen !== false;
		var d = drafts[key] || (drafts[key] = { open: !saved && startOpen, split: false, texts: [] });

		card.innerHTML = '';
		card.classList.toggle('is-open', d.open);

		if (!d.open) {
			if (saved) {
				var box = el('div', 'vb-card__saved');
				box.appendChild(el('span', 'vb-card__label', 'Handwritten card'));
				box.appendChild(el('p', 'vb-card__text', saved));
				var edit = el('button', 'vb-card__link', 'Edit');
				edit.type = 'button';
				edit.addEventListener('click', function () { d.open = true; d.texts = [saved]; render(card); });
				box.appendChild(edit);
				card.appendChild(box);
			} else {
				var add = el('button', 'vb-card__add', '+ Add a handwritten card');
				add.type = 'button';
				add.addEventListener('click', function () { d.open = true; render(card); focusFirst(card); });
				card.appendChild(add);
			}
			card.appendChild(hiddenSaved(saved));
			return;
		}

		// Open: inline form
		var form = el('div', 'vb-card__form');
		var count = d.split && qty > 1 ? qty : 1;
		for (var i = 0; i < count; i++) {
			var id = 'vb-card-' + key.replace(/[^a-z0-9]/gi, '') + '-' + i;
			var label = el('label', 'vb-card__label', count > 1 ? 'Card ' + (i + 1) + ' of ' + count : 'Handwritten card');
			label.setAttribute('for', id);
			var ta = el('textarea', 'vb-card__input');
			ta.id = id;
			ta.rows = 3;
			ta.maxLength = MAX_LEN;
			ta.placeholder = PLACEHOLDER;
			// Plain free text: keep iOS/Android from treating it as a login/address
			// field (their AutoFill bar), but leave spelling help on.
			ta.setAttribute('autocomplete', 'off');
			ta.setAttribute('autocapitalize', 'sentences');
			ta.setAttribute('spellcheck', 'true');
			ta.value = d.texts[i] != null ? d.texts[i] : (i === 0 ? saved : '');
			(function (index, area) {
				area.addEventListener('focus', function () { centreWhenKeyboardUp(area); });
				area.addEventListener('input', function () {
					d.texts[index] = area.value;
					status(card, '');
					schedule(card);
				});
			})(i, ta);
			// Character count sits inside the box's bottom-right corner.
			var field = el('div', 'vb-card__field');
			var meta = el('span', 'vb-card__count');
			meta.setAttribute('aria-hidden', 'true');
			counter(ta, meta);
			field.appendChild(ta);
			field.appendChild(meta);
			form.appendChild(label);
			form.appendChild(field);
		}

		if (qty > 1) {
			var splitWrap = el('label', 'vb-card__split');
			var cb = document.createElement('input');
			cb.type = 'checkbox';
			cb.checked = d.split;
			cb.addEventListener('change', function () {
				collect(card, d);
				d.split = cb.checked;
				render(card);
				focusFirst(card);
			});
			splitWrap.appendChild(cb);
			splitWrap.appendChild(document.createTextNode(' Write a separate card for each of the ' + qty));
			form.appendChild(splitWrap);
		}

		var error = el('p', 'vb-card__error');
		error.setAttribute('role', 'alert');
		error.hidden = true;
		form.appendChild(error);

		var actions = el('div', 'vb-card__actions');
		var saveBtn = el('button', 'btn vb-card__save', 'Save card' + (count > 1 ? 's' : ''));
		saveBtn.type = 'button';
		var cancel = el('button', 'vb-card__link', 'Cancel');
		cancel.type = 'button';
		var stat = el('span', 'vb-card__status');
		stat.setAttribute('aria-live', 'polite');
		stat.style.cssText = 'margin-left:auto;font-size:12px;color:#6b6b6b;';
		actions.appendChild(saveBtn);
		actions.appendChild(cancel);
		actions.appendChild(stat);
		form.appendChild(actions);
		card.appendChild(form);
		card.appendChild(hiddenSaved(saved));

		// Close the box (and keep it closed through drawer re-renders).
		cancel.addEventListener('click', function () {
			clearTimeout(card._vbTimer);
			drafts[card.getAttribute('data-key')] = { open: false, split: false, texts: [] };
			render(card);
		});

		saveBtn.addEventListener('click', function () {
			if (!isDirty(card)) { // unchanged (e.g. Edit, then Save) - just close
				drafts[card.getAttribute('data-key')] = { open: false, split: false, texts: [] };
				render(card);
				return;
			}
			commit(card).catch(function () {});
		});

		// Auto-save when focus leaves the card. A tap on the card's own controls
		// (Save, Cancel, the split checkbox, another card textarea) is left to them.
		form.addEventListener('focusout', function (e) {
			var to = e.relatedTarget || (Date.now() - lastPointerAt < 600 ? lastPointer : null);
			if (to && card.contains(to)) return;
			setTimeout(function () {
				if (!card.isConnected || card.contains(document.activeElement)) return;
				if (isDirty(card)) commit(card).catch(function () {});
			}, 0);
		});
	}

	function hiddenSaved(text) {
		var p = el('p', 'vb-card__saved-text', text);
		p.setAttribute('data-vb-card-saved', '');
		p.hidden = true;
		return p;
	}
	function focusFirst(card) {
		var ta = card.querySelector('.vb-card__input');
		if (ta) ta.focus();
	}

	function init(root) {
		var cards = (root || document).querySelectorAll('[data-vb-card]:not([data-vb-ready])');
		Array.prototype.forEach.call(cards, function (card) {
			card.setAttribute('data-vb-ready', '1');
			var show = card.hasAttribute('data-has-message') || card.hasAttribute('data-gpo');
			var go = function () { card.hidden = false; render(card); };
			if (show) return go();
			isFloral(card.getAttribute('data-handle')).then(function (floral) { if (floral) go(); });
		});
	}

	/* ---------- checkout guard ---------- */

	// Same button after any re-render: the original if still on the page, else
	// the first visible match for its name / class.
	function findAgain(btn) {
		if (btn.isConnected) return btn;
		var sel = btn.getAttribute('name') ? '[name="' + btn.getAttribute('name') + '"]'
			: (btn.className ? '.' + String(btn.className).trim().split(/\s+/).join('.') : null);
		if (!sel) return null;
		var all = document.querySelectorAll(sel);
		for (var i = 0; i < all.length; i++) if (all[i].offsetParent !== null) return all[i];
		return all[0] || null;
	}

	var bypass = null;

	document.addEventListener('pointerdown', function (e) {
		lastPointer = e.target;
		lastPointerAt = Date.now();
		// iPad Safari keeps focus in the textarea when a blank area is tapped, so
		// focusout never fires there - treat any tap outside the card as leaving it.
		var open = document.querySelectorAll('[data-vb-card].is-open');
		Array.prototype.forEach.call(open, function (card) {
			if (!card.contains(e.target) && isDirty(card)) commit(card).catch(function () {});
		});
	}, true);
	window.addEventListener('pagehide', function () { leaving = true; });
	window.addEventListener('beforeunload', function () { leaving = true; });

	window.addEventListener('click', function (e) {
		var btn = e.target && e.target.closest ? e.target.closest(LEAVE_SELECTOR) : null;
		if (!btn) return;
		if (bypass === btn) { bypass = null; return; }

		var dirty = Array.prototype.filter.call(document.querySelectorAll('[data-vb-card].is-open'), isDirty);
		if (!dirty.length && !inflight.length) return;

		e.preventDefault();
		e.stopImmediatePropagation();
		if (holding) return; // already saving for an earlier click
		holding = true;

		// One card at a time - each save reads and rewrites the cart.
		var chain = Promise.all(inflight.map(function (p) { return p.catch(function () {}); }));
		dirty.forEach(function (card) { chain = chain.then(function () { return commit(card); }); });

		chain.then(function () {
			holding = false;
			var again = findAgain(btn);
			if (!again) { refreshCart(); return; }
			bypass = again;
			again.click();
			// If checkout didn't go ahead (e.g. Zapiet still needs a date), bring
			// the cart view up to date with the saved cards.
			setTimeout(function () {
				bypass = null;
				if (!leaving) refreshCart();
			}, 1500);
		}, function () {
			holding = false; // error is shown on the card; customer can retry
			var bad = document.querySelector('.vb-card__error:not([hidden])');
			if (bad && bad.scrollIntoView) bad.scrollIntoView({ block: 'center' });
		});
	}, true);

	/* ---------- boot: watch the drawer + cart page for re-renders ---------- */

	function watch() {
		var containers = document.querySelectorAll('.js-drawer [data-cart-container], [data-cartpage-container]');
		Array.prototype.forEach.call(containers, function (container) {
			init(container);
			new MutationObserver(function () { init(container); }).observe(container, { childList: true, subtree: true });
		});
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
	else watch();
})();
